import React, { useState, useEffect, useCallback } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Bell, BellRing, Info, MapPin, Users, UserCheck, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../lib/api';

const CATEGORY_COLORS = {
  Urlaub: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  Änderungsanträge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  Zeiterfassung: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  Workflows: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  Compliance: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300',
  System: 'bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
};

const SCOPE_LABELS = {
  all: 'Alle',
  branch: 'Niederlassung',
  location: 'Standort',
  own: 'Eigene',
};

function RecipientCell({ entry }) {
  if (entry.recipient_kind === 'user') {
    return (
      <div className="flex items-start gap-2">
        <UserCheck className="h-4 w-4 mt-0.5 text-blue-600 flex-shrink-0" />
        <span className="text-sm">{entry.recipient_description}</span>
      </div>
    );
  }
  if (entry.recipient_kind === 'role_override') {
    return (
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 mt-0.5 text-red-600 flex-shrink-0" />
        <span className="text-sm">{entry.recipient_description}</span>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <Users className="h-4 w-4 mt-0.5 text-indigo-600 flex-shrink-0" />
        <div className="text-sm">
          <p>{entry.recipient_description}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Berechtigung: <code className="font-mono text-[11px]">{entry.required_permission}</code>
          </p>
        </div>
      </div>
      {entry.matching_roles?.length > 0 && (
        <div className="flex flex-wrap gap-1 pl-6">
          {entry.matching_roles.map((r) => (
            <Badge
              key={r.id}
              variant="secondary"
              className="text-[11px] font-normal"
              style={r.color ? { borderLeft: `3px solid ${r.color}` } : undefined}
              title={`${r.user_count} Personen · Scope ${SCOPE_LABELS[r.scope_type] || r.scope_type}`}
            >
              {r.name}
              <span className="ml-1 opacity-60">({r.user_count})</span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NotificationMatrix({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.notificationMatrix();
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Matrix konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-4">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-96" />
        </div>
      </MaybeLayout>
    );
  }

  const entries = data?.entries || [];
  const grouped = entries.reduce((acc, e) => {
    (acc[e.category] = acc[e.category] || []).push(e);
    return acc;
  }, {});

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-6" data-testid="notification-matrix-page">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <BellRing className="h-6 w-6 text-primary" />
            Benachrichtigungs-Matrix
          </h1>
          <p className="text-muted-foreground mt-1">
            Übersicht aller automatisch versendeten In-App-Benachrichtigungen und deren Empfänger.
          </p>
        </div>

        <Alert className="border-blue-500/40 bg-blue-50 dark:bg-blue-900/10">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle>Read-Only Übersicht</AlertTitle>
          <AlertDescription className="text-sm">
            Diese Seite zeigt, wer welche System-Benachrichtigung bekommt. Empfänger basierend auf
            Berechtigungen werden gegen die aktuellen Rollen unter <strong>Berechtigungen</strong> aufgelöst.
            Persönliche Einstellungen zu Notifications können Benutzer unter <strong>Mein Profil</strong>
            steuern.
          </AlertDescription>
        </Alert>

        {Object.entries(grouped).map(([category, items]) => (
          <Card key={category} data-testid={`matrix-category-${category}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Bell className="h-4 w-4" />
                <span>{category}</span>
                <Badge className={CATEGORY_COLORS[category] || 'bg-slate-100'}>
                  {items.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[22%]">Benachrichtigung</TableHead>
                      <TableHead className="w-[28%]">Auslöser</TableHead>
                      <TableHead className="w-[38%]">Empfänger</TableHead>
                      <TableHead className="w-[12%] text-right">Eigenschaften</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((entry, idx) => (
                      <TableRow key={`${entry.type}-${idx}`} data-testid={`matrix-row-${entry.type}-${idx}`}>
                        <TableCell className="align-top">
                          <div className="font-medium text-sm">{entry.title}</div>
                          <code className="text-[11px] text-muted-foreground font-mono">
                            {entry.type}
                          </code>
                        </TableCell>
                        <TableCell className="align-top text-sm">{entry.trigger}</TableCell>
                        <TableCell className="align-top">
                          <RecipientCell entry={entry} />
                        </TableCell>
                        <TableCell className="align-top text-right">
                          <div className="flex flex-col items-end gap-1">
                            {entry.location_scoped && (
                              <Badge variant="outline" className="text-[10px]">
                                <MapPin className="h-3 w-3 mr-1" />
                                Standort
                              </Badge>
                            )}
                            {entry.push_enabled ? (
                              <Badge variant="outline" className="text-[10px]">
                                <BellRing className="h-3 w-3 mr-1" />
                                Push
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] opacity-60">
                                In-App
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="text-xs text-muted-foreground pt-2">
          {entries.length} Benachrichtigungs-Typ{entries.length !== 1 ? 'en' : ''} ·{' '}
          {data?.role_count || 0} Rollen konfiguriert
        </div>
      </div>
    </MaybeLayout>
  );
}
